import { useState, useEffect, useCallback } from "react";
import axios from "axios";

interface ReportConfigResponse {
  timezone: string;
  subscriber: string[];
  report_cron: string;
  email_cron: string;
  config: Record<string, unknown>;
  namespace: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  report_start_time: string;
  report_end_time: string;
}

interface ParsedConfiguration extends ReportConfigResponse {
  report_send_at: string;
  email_send_at: string;
}

export const cronToTime = (cronExpression: string): string | null => {
  try {
    // eslint-disable-next-line no-console
    console.log("cron Expression: ", cronExpression);
    const parts = cronExpression.split(" ");
    if (parts.length < 5) {
      throw new Error("Invalid cron");
    }

    const [minute, hour] = parts;

    if (minute === "*" || hour === "*") {
      throw new Error('"Multiple times daily"');
    }

    const numHour = parseInt(hour);
    const numMinute = parseInt(minute);

    if (isNaN(numHour) || isNaN(numMinute)) {
      throw new Error("cron is not valid");
    }

    return `${numHour
      .toString()
      .padStart(2, "0")}:${numMinute.toString().padStart(2, "0")}`;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Cron convert to time error");
    return null;
  }
};

export const timeToCron = (timeString: string): string => {
  const [hours, minutes] = timeString.split(":").map(Number);

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    throw new Error("Invalid time format. Please use HH:MM in 24-hour format.");
  }

  return `${minutes} ${hours} * * *`;
};

const useReportConfigData = (namespace: string) => {
  const [reportConfig, setReportConfig] = useState<
    ParsedConfiguration | undefined
  >(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchReportConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `https://api.onemetrics-report-service.hardware-inno.gcpprodau.tbm.sh/api/v1/report/config/${namespace}`,
        {
          headers: {
            accept: "application/json",
          },
        },
      );
      const data: ReportConfigResponse = JSON.parse(res.data);

      // eslint-disable-next-line no-console
      console.log(
        "CRON Value to 24HRs: ",
        cronToTime(data.report_cron),
        cronToTime(data.email_cron),
      );
      setReportConfig({
        ...data,
        report_send_at: cronToTime(data.report_cron) || "",
        email_send_at: cronToTime(data.email_cron) || "",
      });
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("An unknown error occurred"),
      );
      setReportConfig(undefined);
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [namespace]);

  useEffect(() => {
    fetchReportConfig();
  }, [fetchReportConfig]);

  const refetch = useCallback(() => {
    fetchReportConfig();
  }, [fetchReportConfig]);

  return { reportConfig, loading, error, refetch };
};

export default useReportConfigData;
